import { useState, useEffect } from "react";
import { toast } from "sonner";
import { 
  Bell, 
  Mail, 
  MessageSquare, 
  Clock, 
  Save, 
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Shield,
  Image,
  FileText,
  Gauge,
  Layout,
  Zap,
  MapPin
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Monitor configurations
const MONITORS = [
  { id: "uptime", name: "Uptime Monitor", icon: Zap, color: "text-green-400" },
  { id: "images", name: "Image Monitor", icon: Image, color: "text-blue-400" },
  { id: "seo", name: "Meta/Alt Monitor", icon: FileText, color: "text-purple-400" },
  { id: "onpage", name: "OnPage Monitor", icon: Layout, color: "text-orange-400" },
  { id: "sections", name: "Section Monitor", icon: Shield, color: "text-cyan-400" },
  { id: "pagespeed", name: "PageSpeed Monitor", icon: Gauge, color: "text-yellow-400" },
  { id: "techseo", name: "TechSEO Monitor", icon: AlertTriangle, color: "text-red-400" },
  { id: "gmb", name: "GMB Monitor", icon: MapPin, color: "text-pink-400" },
];

// Frequency options
const FREQUENCY_OPTIONS = [
  { value: "5m", label: "Every 5 minutes" },
  { value: "15m", label: "Every 15 minutes" },
  { value: "30m", label: "Every 30 minutes" },
  { value: "1h", label: "Every 1 hour" },
  { value: "3h", label: "Every 3 hours" },
  { value: "6h", label: "Every 6 hours" },
  { value: "12h", label: "Every 12 hours" },
  { value: "24h", label: "Every 24 hours" },
  { value: "48h", label: "Every 48 hours" },
  { value: "72h", label: "Every 72 hours" },
  { value: "7d", label: "Every 7 days" },
];

// Alert trigger options per monitor
const ALERT_TRIGGERS = {
  uptime: [
    { id: "site_down", label: "Site goes DOWN" },
    { id: "site_up", label: "Site comes back UP" },
    { id: "ssl_expiring", label: "SSL certificate expiring (< 30 days)" },
    { id: "ssl_expired", label: "SSL certificate expired" },
    { id: "response_slow", label: "Response time > 5 seconds" },
  ],
  images: [
    { id: "new_images", label: "New images detected" },
    { id: "images_removed", label: "Images removed" },
    { id: "scan_complete", label: "Scan completed" },
  ],
  seo: [
    { id: "missing_title", label: "Pages missing title" },
    { id: "missing_description", label: "Pages missing description" },
    { id: "missing_alt", label: "Images missing alt text" },
    { id: "threshold_exceeded", label: "Issue threshold exceeded (configurable %)" },
    { id: "scan_complete", label: "Scan completed" },
  ],
  onpage: [
    { id: "low_eeat_score", label: "Low EEAT score (< 50)" },
    { id: "low_hcu_score", label: "Low HCU score (< 50)" },
    { id: "scan_complete", label: "Scan completed" },
  ],
  sections: [
    { id: "missing_sections", label: "Missing critical sections" },
    { id: "scan_complete", label: "Scan completed" },
  ],
  pagespeed: [
    { id: "low_performance", label: "Low performance score (< 50)" },
    { id: "low_accessibility", label: "Low accessibility score (< 50)" },
    { id: "low_seo", label: "Low SEO score (< 50)" },
    { id: "cls_issue", label: "CLS issues detected" },
    { id: "lcp_issue", label: "LCP issues detected" },
    { id: "scan_complete", label: "Scan completed" },
  ],
  techseo: [
    { id: "critical_issues", label: "Critical issues found" },
    { id: "broken_links", label: "Broken links detected" },
    { id: "scan_complete", label: "Scan completed" },
  ],
  gmb: [
    { id: "review_received", label: "New review received" },
    { id: "rating_drop", label: "Rating dropped" },
    { id: "scan_complete", label: "Scan completed" },
  ],
};

export function AlertRules() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    notifications: {
      email: { enabled: false, addresses: "" },
      slack: { enabled: false, webhookUrl: "" },
    },
    frequencies: {},
    triggers: {},
  });

  // Initialize default frequencies and triggers
  useEffect(() => {
    const defaultFrequencies = {};
    const defaultTriggers = {};
    
    MONITORS.forEach(m => {
      defaultFrequencies[m.id] = m.id === "uptime" ? "5m" : 
                                 m.id === "images" ? "6h" :
                                 m.id === "seo" ? "24h" :
                                 m.id === "onpage" ? "48h" :
                                 m.id === "sections" ? "72h" :
                                 m.id === "pagespeed" ? "7d" : "24h";
      defaultTriggers[m.id] = [];
    });

    fetchConfig(defaultFrequencies, defaultTriggers);
  }, []);

  const fetchConfig = async (defaultFrequencies, defaultTriggers) => {
    try {
      const response = await fetch(`${API_URL}/api/alert-rules`);
      if (response.ok) {
        const data = await response.json();
        setConfig({
          notifications: data.notifications || {
            email: { enabled: false, addresses: "" },
            slack: { enabled: false, webhookUrl: "" },
          },
          frequencies: { ...defaultFrequencies, ...data.frequencies },
          triggers: { ...defaultTriggers, ...data.triggers },
        });
      } else {
        // Use defaults if no config exists
        setConfig({
          notifications: {
            email: { enabled: false, addresses: "" },
            slack: { enabled: false, webhookUrl: "" },
          },
          frequencies: defaultFrequencies,
          triggers: defaultTriggers,
        });
      }
    } catch (error) {
      console.error("Failed to fetch config:", error);
      setConfig({
        notifications: {
          email: { enabled: false, addresses: "" },
          slack: { enabled: false, webhookUrl: "" },
        },
        frequencies: defaultFrequencies,
        triggers: defaultTriggers,
      });
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/alert-rules`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(config),
      });
      
      if (response.ok) {
        toast.success("Alert rules saved successfully");
      } else {
        throw new Error("Failed to save");
      }
    } catch (error) {
      toast.error("Failed to save alert rules");
    } finally {
      setSaving(false);
    }
  };

  const updateNotification = (type, field, value) => {
    setConfig(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [type]: {
          ...prev.notifications[type],
          [field]: value,
        },
      },
    }));
  };

  const updateFrequency = (monitorId, value) => {
    setConfig(prev => ({
      ...prev,
      frequencies: {
        ...prev.frequencies,
        [monitorId]: value,
      },
    }));
  };

  const toggleTrigger = (monitorId, triggerId) => {
    setConfig(prev => {
      const currentTriggers = prev.triggers[monitorId] || [];
      const newTriggers = currentTriggers.includes(triggerId)
        ? currentTriggers.filter(t => t !== triggerId)
        : [...currentTriggers, triggerId];
      
      return {
        ...prev,
        triggers: {
          ...prev.triggers,
          [monitorId]: newTriggers,
        },
      };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-100px)]">
        <RefreshCw className="w-6 h-6 animate-spin text-zinc-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8 max-w-5xl mx-auto" data-testid="alert-rules-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bell className="w-6 h-6 text-orange-400" />
          <h1 className="text-2xl font-bold text-zinc-100 font-mono">Alert Rules</h1>
        </div>
        <button
          onClick={saveConfig}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-mono text-sm transition-colors disabled:opacity-50"
          data-testid="save-alert-rules-btn"
        >
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Changes
        </button>
      </div>

      {/* Notification Channels */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6" data-testid="notification-channels">
        <h2 className="text-lg font-semibold text-zinc-100 font-mono mb-4 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-blue-400" />
          Notification Channels
        </h2>
        
        <div className="space-y-6">
          {/* Email */}
          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="email-enabled"
                checked={config.notifications.email.enabled}
                onChange={() => updateNotification("email", "enabled", true)}
                className="w-4 h-4 accent-orange-500"
                data-testid="email-enabled-radio"
              />
              <Mail className="w-5 h-5 text-zinc-400" />
              <span className="text-zinc-300 font-mono">Email Notifications</span>
              <span className="text-xs text-green-400 font-mono">ENABLED</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer ml-6">
              <input
                type="radio"
                name="email-enabled"
                checked={!config.notifications.email.enabled}
                onChange={() => updateNotification("email", "enabled", false)}
                className="w-4 h-4 accent-orange-500"
              />
              <span className="text-zinc-500 font-mono text-sm">Disabled</span>
            </label>
            {config.notifications.email.enabled && (
              <div className="ml-6 mt-2">
                <input
                  type="text"
                  placeholder="email1@example.com, email2@example.com"
                  value={config.notifications.email.addresses}
                  onChange={(e) => updateNotification("email", "addresses", e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-zinc-100 font-mono text-sm placeholder-zinc-600 focus:outline-none focus:border-orange-500"
                  data-testid="email-addresses-input"
                />
                <p className="text-xs text-zinc-600 mt-1 font-mono">Separate multiple emails with commas</p>
              </div>
            )}
          </div>

          {/* Slack */}
          <div className="space-y-3 pt-4 border-t border-zinc-800">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="slack-enabled"
                checked={config.notifications.slack.enabled}
                onChange={() => updateNotification("slack", "enabled", true)}
                className="w-4 h-4 accent-orange-500"
                data-testid="slack-enabled-radio"
              />
              <MessageSquare className="w-5 h-5 text-zinc-400" />
              <span className="text-zinc-300 font-mono">Slack Notifications</span>
              <span className="text-xs text-green-400 font-mono">ENABLED</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer ml-6">
              <input
                type="radio"
                name="slack-enabled"
                checked={!config.notifications.slack.enabled}
                onChange={() => updateNotification("slack", "enabled", false)}
                className="w-4 h-4 accent-orange-500"
              />
              <span className="text-zinc-500 font-mono text-sm">Disabled</span>
            </label>
            {config.notifications.slack.enabled && (
              <div className="ml-6 mt-2">
                <input
                  type="text"
                  placeholder="https://hooks.slack.com/services/..."
                  value={config.notifications.slack.webhookUrl}
                  onChange={(e) => updateNotification("slack", "webhookUrl", e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-2 text-zinc-100 font-mono text-sm placeholder-zinc-600 focus:outline-none focus:border-orange-500"
                  data-testid="slack-webhook-input"
                />
                <p className="text-xs text-zinc-600 mt-1 font-mono">Enter your Slack webhook URL</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Monitor Frequencies */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6" data-testid="monitor-frequencies">
        <h2 className="text-lg font-semibold text-zinc-100 font-mono mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-cyan-400" />
          Scan Frequencies
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {MONITORS.map((monitor) => {
            const Icon = monitor.icon;
            return (
              <div key={monitor.id} className="bg-zinc-800/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Icon className={`w-4 h-4 ${monitor.color}`} />
                  <span className="text-zinc-200 font-mono text-sm">{monitor.name}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {FREQUENCY_OPTIONS.map((freq) => (
                    <label
                      key={freq.value}
                      className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer text-xs font-mono transition-colors ${
                        config.frequencies[monitor.id] === freq.value
                          ? "bg-orange-500/20 text-orange-400 border border-orange-500/50"
                          : "bg-zinc-700/50 text-zinc-400 border border-zinc-700 hover:border-zinc-600"
                      }`}
                    >
                      <input
                        type="radio"
                        name={`freq-${monitor.id}`}
                        value={freq.value}
                        checked={config.frequencies[monitor.id] === freq.value}
                        onChange={() => updateFrequency(monitor.id, freq.value)}
                        className="sr-only"
                      />
                      {freq.label}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Alert Triggers */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6" data-testid="alert-triggers">
        <h2 className="text-lg font-semibold text-zinc-100 font-mono mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-yellow-400" />
          Alert Triggers
        </h2>
        <p className="text-zinc-500 text-sm font-mono mb-6">Select which events should trigger notifications</p>
        
        <div className="space-y-6">
          {MONITORS.map((monitor) => {
            const Icon = monitor.icon;
            const triggers = ALERT_TRIGGERS[monitor.id] || [];
            const selectedTriggers = config.triggers[monitor.id] || [];
            
            return (
              <div key={monitor.id} className="border-b border-zinc-800 pb-4 last:border-0">
                <div className="flex items-center gap-2 mb-3">
                  <Icon className={`w-4 h-4 ${monitor.color}`} />
                  <span className="text-zinc-200 font-mono text-sm font-semibold">{monitor.name}</span>
                  {selectedTriggers.length > 0 && (
                    <span className="text-xs bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded font-mono">
                      {selectedTriggers.length} active
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 ml-6">
                  {triggers.map((trigger) => (
                    <label
                      key={trigger.id}
                      className={`flex items-center gap-2 px-3 py-2 rounded cursor-pointer text-sm font-mono transition-colors ${
                        selectedTriggers.includes(trigger.id)
                          ? "bg-green-500/10 text-green-400 border border-green-500/30"
                          : "bg-zinc-800/50 text-zinc-500 border border-zinc-700/50 hover:border-zinc-600"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedTriggers.includes(trigger.id)}
                        onChange={() => toggleTrigger(monitor.id, trigger.id)}
                        className="w-3.5 h-3.5 accent-green-500"
                      />
                      {trigger.label}
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Status Summary */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              {config.notifications.email.enabled ? (
                <CheckCircle2 className="w-4 h-4 text-green-400" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-zinc-600" />
              )}
              <span className={`text-sm font-mono ${config.notifications.email.enabled ? "text-green-400" : "text-zinc-600"}`}>
                Email {config.notifications.email.enabled ? "ON" : "OFF"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {config.notifications.slack.enabled ? (
                <CheckCircle2 className="w-4 h-4 text-green-400" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-zinc-600" />
              )}
              <span className={`text-sm font-mono ${config.notifications.slack.enabled ? "text-green-400" : "text-zinc-600"}`}>
                Slack {config.notifications.slack.enabled ? "ON" : "OFF"}
              </span>
            </div>
          </div>
          <span className="text-xs text-zinc-600 font-mono">
            {Object.values(config.triggers).flat().length} triggers active
          </span>
        </div>
      </div>
    </div>
  );
}

export default AlertRules;
